// iter689 — Phase B SSoT banner. Drop into any form/page that
// previously re-asked the operator for Company Name / VAT / CR /
// Address / Phone / Email. The banner reads the centrally-stored
// values from /api/pdf-exports/branding once on mount and shows a
// compact "✓ Using info from Branding Settings — Edit" chip with
// a one-click link to the central editor.
//
// Important: this component is non-blocking. If branding fetch fails
// or returns no data, the banner self-hides and lets the page render
// its own legacy inputs. That keeps backwards-compat with old tenants
// who haven't yet completed the Country Setup Wizard.
//
// Usage:
//     <SSoTCompanyInfoBanner />
//     // ... rest of form, with company name / VAT inputs REMOVED
//     // because they're now sourced from branding centrally.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Edit, ChevronDown, ChevronUp, RefreshCw, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import api from '@/lib/api';

const FIELD_LABELS = [
  ['company_name', 'Company name'],
  ['company_vat', 'VAT number'],
  ['cr_number', 'CR number'],
  ['company_address', 'Address'],
  ['company_phone', 'Phone'],
  ['company_email', 'Email'],
];

export function SSoTCompanyInfoBanner({
  className = '',
  /** Override the destination of the "Edit" link. Defaults to /branding. */
  editHref = '/branding',
  /** Limit which fields to preview. Default: company_name, VAT, CR. */
  previewFields = ['company_name', 'company_vat', 'cr_number'],
  /** Display label tweak, e.g. "Invoice details" instead of "Company info". */
  scopeLabel = 'Company info',
  'data-testid': testId = 'ssot-company-banner',
}) {
  const [branding, setBranding] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // iter691 — "Sync to all branches" lets an admin push the central
  // receipt header / footer / country defaults to every branch in
  // one click, without opening Settings → Branches and editing each
  // row by hand.
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/pdf-exports/branding');
        if (alive) setBranding(data || {});
      } catch {
        if (alive) setBranding({});
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Hide entirely if no central data exists yet — the page's own
  // legacy inputs should still work.
  if (!loaded) return null;
  if (!branding || !branding.company_name) return null;

  const preview = previewFields
    .map(k => branding[k])
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ');

  const filledCount = FIELD_LABELS.filter(([k]) => branding[k]).length;

  // iter691 — One-click sync: push receipt header / footer / country
  // from central branding to every branch override. Backend rejects
  // non-admin callers (403). Toasts the result either way.
  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/pdf-exports/branding/sync-to-branches');
      toast.success(data?.message || `Synced ${data?.updated || 0}/${data?.total || 0} branches.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed — admin/manager only.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/70 text-[12px] ${className}`}
      data-testid={testId}
    >
      <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-emerald-900">{scopeLabel} from Branding Settings</span>
          <span className="text-emerald-700/80 text-[11px]">
            {filledCount}/{FIELD_LABELS.length} fields filled
          </span>
        </div>
        {preview && (
          <div className="text-emerald-800/90 truncate text-[11px]">{preview}</div>
        )}
        {expanded && (
          <ul className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-emerald-900/90">
            {FIELD_LABELS.map(([k, label]) => (
              <li key={k} className="flex items-baseline gap-1">
                <span className="text-emerald-700/80 shrink-0">{label}:</span>
                <span className="font-mono truncate">{branding[k] || <em className="text-emerald-700/50">not set</em>}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="p-1 rounded hover:bg-emerald-100 cursor-help inline-flex" data-testid={`${testId}-help`}>
                <Info size={11} className="text-emerald-700/70" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[260px] text-[11px] leading-snug">
              <p className="font-semibold mb-1">Single Source of Truth</p>
              <p>
                Company name, VAT, CR &amp; address are entered <strong>once</strong> in Branding Settings and reused on every invoice, quote, ZATCA QR, and report.
              </p>
              <p className="mt-1.5">
                Click <strong>Sync</strong> to push the central receipt header / footer / country to every branch override in one go.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-800 text-[11px] font-semibold disabled:opacity-60"
          data-testid={`${testId}-sync-btn`}
          title="Push central receipt header/footer/country to every branch"
        >
          <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing…' : 'Sync'}
        </button>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="p-1 rounded hover:bg-emerald-100"
          title={expanded ? 'Collapse' : 'See all fields'}
          data-testid={`${testId}-toggle`}
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <Link
          to={editHref}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
          data-testid={`${testId}-edit-link`}
        >
          <Edit size={11} />
          Edit
        </Link>
      </div>
    </div>
  );
}

export default SSoTCompanyInfoBanner;
